import { useEffect, type FunctionComponent } from 'react';
import { FollowPropertySwitch } from '../../../components/PropertyRelationship/FollowPropertySwitch/FollowPropertySwitch';
import { useFollowedProperties } from '../../hooks/useFollowedProperties';
import { usePropertyPageModalContext } from '../../contexts/PropertyPageModalContext/PropertyPageModalContext';
import { useUrlContext } from '../../../contexts/UrlContext/UrlContext';
import { usePropertyId } from '../../../contexts/PropertyIdContext/PropertyIdContext';
import { useJoinUsUrl } from '../../../hooks/useJoinUsUrl';
import { useTrackPropertyRelationshipModal } from './useTrackPropertyRelationshipModal';
import { useWaitForHydrationWorkaround } from './useWaitForHydrationWorkaround';
import { useAccountQuery } from '../../../hooks/useAccountQuery';
import { isNotNullOrUndefinedValue } from '../../../helpers/isNotNullOrUndefinedValue';
import { updatePropertyRelationshipModalPageSource } from '../../../components/PropertyRelationship/UpdatePropertyRelationshipModal/UpdatePropertyRelationshipModalPageSource';
import { followPropertyModalPageSource } from '../../../components/PropertyRelationship/FollowPropertyModal/FollowPropertyModalPageSource';
import { clickThroughSourceTracking } from '../../../analytics/click-through-tracking/clickThroughSourceTracking';
import { PROPERTY_DETAIL_PAGE_TRACKING_SOURCE } from './tracking';
import { replace } from '@rea-argonaut/link';
import { assignLocation } from '../../../helpers/assignLocation';
import { mapPropertyRelationshipToEnum } from '../../../components/PropertyRelationship/PropertyRelationship';

export const openSetRelationshipModalUrlHash = '#open-set-relationship-modal';

export const FollowProperty: FunctionComponent = () => {
  const { followedProperties, loading } = useFollowedProperties();
  const { displayModal } = usePropertyPageModalContext();
  const url = useUrlContext();
  const propertyId = usePropertyId();
  const joinUsUrl = useJoinUsUrl(`${url.pathname}${openSetRelationshipModalUrlHash}`);
  const trackPropertyRelationshipModal = useTrackPropertyRelationshipModal();
  const isHydrated = useWaitForHydrationWorkaround();

  const { data } = useAccountQuery();

  const signedIn = isNotNullOrUndefinedValue(data?.account);

  const propertyRelationship = followedProperties?.find(
    ({ id }) => id === propertyId,
  )?.relationship;

  const propertyRelationshipEnum = mapPropertyRelationshipToEnum(propertyRelationship);

  const isFollowed = propertyRelationship !== undefined;

  useEffect(() => {
    if (isHydrated && signedIn && url.hash === openSetRelationshipModalUrlHash) {
      if (isFollowed) {
        displayModal({ name: 'updatePropertyRelationshipModal' });
        trackPropertyRelationshipModal({
          user_action: 'opened',
          modal_name: updatePropertyRelationshipModalPageSource,
          screen_name: 'property_page',
          in_page_source: ['property_page', 'url_hash'].join('__'),
        });
      } else {
        displayModal({ name: 'followPropertyModal' });
        trackPropertyRelationshipModal({
          user_action: 'opened',
          modal_name: followPropertyModalPageSource,
          screen_name: 'property_page',
          in_page_source: ['property_page', 'url_hash'].join('__'),
        });
      }

      replace(url.pathname + url.search);
    }
  }, [signedIn, isHydrated, displayModal, isFollowed, trackPropertyRelationshipModal, url]);

  const onFollowSwitchChange = (checked?: boolean) => {
    if (!signedIn) {
      clickThroughSourceTracking({
        pathname: '/join-us/',
        page: PROPERTY_DETAIL_PAGE_TRACKING_SOURCE,
        element: [PROPERTY_DETAIL_PAGE_TRACKING_SOURCE, 'follow_property_toggle'].join('__'),
        sourceType: 'web',
      });
      assignLocation(joinUsUrl);
      return;
    }

    if (checked) {
      displayModal({ name: 'followPropertyModal' });
      trackPropertyRelationshipModal({
        user_action: 'opened',
        modal_name: followPropertyModalPageSource,
        screen_name: 'property_page',
        in_page_source: ['property_page', 'follow_property_toggle'].join('__'),
      });
    }
    if (!checked) {
      displayModal({ name: 'updatePropertyRelationshipModal' });
      trackPropertyRelationshipModal({
        user_action: 'opened',
        modal_name: updatePropertyRelationshipModalPageSource,
        screen_name: 'property_page',
        in_page_source: ['property_page', 'follow_property_toggle'].join('__'),
      });
    }
  };

  return (
    <FollowPropertySwitch
      followed={isFollowed}
      relationship={propertyRelationshipEnum}
      onToggleChange={onFollowSwitchChange}
      disabled={loading}
    />
  );
};
